/**
 * TradePreviewCard — Round 96k (Jul 2026).
 *
 * "Would this trade help my portfolio?" widget. Sits at the TOP of
 * the Stock Modal → My Portfolio tab, but ONLY when the user does
 * NOT already own the ticker in the currently-scoped portfolio (per
 * the user's spec: once you own the stock the tab fills up with
 * lot rows and there's no room). Powered by
 * `/api/portfolio/blend-preview` which reuses the exact same
 * weighting math as the Portfolio Blend snapshot so before/after
 * values are apples-to-apples.
 *
 * Inputs:  shares (positive number), price (defaults to live cached
 *          price; user can override to model a limit buy).
 * Outputs: current blend yield vs projected yield, current annual
 *          dividend income vs projected income, quality score
 *          delta — each with the same green-up / rose-down arrow
 *          grammar the Portfolio-page Benchmark Comparison and the
 *          new Blend Compare 3M chip use.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Calculator, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPct = (v, d = 2) => (v == null || !isFinite(v)) ? '—' : `${Number(v).toFixed(d)}%`;
const fmt$ = (v) => (v == null || !isFinite(v)) ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (v, kind) => {
  if (v == null || !isFinite(v)) return '—';
  const n = Number(v);
  const abs = Math.abs(n);
  if (kind === 'pct') return `${n >= 0 ? '+' : '-'}${abs.toFixed(2)}%`;
  if (kind === 'money') return `${n >= 0 ? '+' : '-'}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${n >= 0 ? '+' : '-'}${abs.toFixed(1)}`;
};

function DeltaChip({ value, kind, label }) {
  if (value == null || !isFinite(value)) return null;
  const n = Number(value);
  const zeroish = Math.abs(n) < (kind === 'pct' ? 0.005 : 0.005);
  const Arrow = zeroish ? Minus : (n > 0 ? TrendingUp : TrendingDown);
  const tone = zeroish
    ? 'text-muted-foreground bg-muted/60'
    : (n > 0 ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10' : 'text-rose-600 dark:text-rose-400 bg-rose-500/10');
  return (
    <div className={`flex items-center gap-1 rounded-md px-2 py-1 ${tone}`}>
      <Arrow className="w-3 h-3" />
      <span className="text-[10px] font-mono font-bold">{fmtSigned(n, kind)}</span>
      {label && <span className="text-[9px] uppercase tracking-wider opacity-80">{label}</span>}
    </div>
  );
}

export default function TradePreviewCard({ ticker, currentPrice, dividendYield, dividendCagr5y, ownsTicker, portfolioId, onApplyTrade }) {
  const [shares, setShares] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPortfolio, setHasPortfolio] = useState(true);
  // Round 96s (Jul 2026) — Payback sparkline is now collapsible.
  // Defaults to CLOSED so the "Add Position" form remains in view.
  const [chartOpen, setChartOpen] = useState(false);

  // Debounced fetch — recomputes projection whenever shares or
  // price changes (and shares > 0).
  const fetchPreview = useCallback(async (n, p) => {
    if (!ticker || !(n > 0)) { setPreview(null); return; }
    setLoading(true);
    try {
      const params = { ticker, shares: n };
      if (p && p > 0) params.price = p;
      if (portfolioId && portfolioId !== 'all') params.portfolio_id = portfolioId;
      const { data } = await axios.get(`${API}/portfolio/blend-preview`, { params, withCredentials: true });
      if (data?.error) {
        setPreview(null);
      } else {
        setPreview(data || null);
        if (data && data.before && (data.before.total_value ?? 0) === 0) setHasPortfolio(false);
      }
    } catch {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [ticker, portfolioId]);

  useEffect(() => {
    const n = Number(shares);
    const p = Number(priceInput) || Number(currentPrice) || 0;
    const t = setTimeout(() => fetchPreview(n, p), 300);
    return () => clearTimeout(t);
  }, [shares, priceInput, currentPrice, fetchPreview]);

  // Do NOT render if the user already owns the stock in this
  // portfolio — the tab is already crowded with lot rows.
  if (ownsTicker) return null;

  const effectivePrice = Number(priceInput) || Number(currentPrice) || 0;

  return (
    <div
      className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/5 dark:bg-fuchsia-500/10 p-3 mb-4"
      data-testid="trade-preview-card"
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Calculator className="w-4 h-4 text-fuchsia-500 shrink-0" />
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-600 dark:text-fuchsia-400">
          Would this trade help my portfolio?
        </h3>
        <p className="text-[10px] text-muted-foreground italic hidden md:inline">
          Live projection · reuses your Portfolio Blend math
        </p>
      </div>

      {!hasPortfolio ? (
        <p className="text-[11px] text-muted-foreground">
          Add at least one position in another stock first — a blend needs holdings to compare against.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">
                Hypothetical shares
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="e.g. 10"
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm font-mono"
                data-testid="trade-preview-shares"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">
                Buy price {currentPrice > 0 && <span className="opacity-60 normal-case">(default {fmt$(currentPrice)})</span>}
              </label>
              <input
                type="number"
                min="0"
                step="any"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder={currentPrice > 0 ? String(currentPrice) : 'e.g. 425'}
                className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm font-mono"
                data-testid="trade-preview-price"
              />
            </div>
          </div>

          {loading && (
            <p className="text-[10px] text-muted-foreground italic mb-2">Recomputing projection…</p>
          )}

          {preview && preview.before && preview.after && Number(shares) > 0 && (
            <div className="space-y-1.5">
              {/* Notional row — makes the projection tangible. */}
              <p className="text-[10px] text-muted-foreground">
                Adding <span className="font-mono font-bold text-foreground">{Number(shares).toLocaleString(undefined, { maximumFractionDigits: 4 })}</span> sh of{' '}
                <span className="font-mono font-bold text-foreground">{ticker}</span> at{' '}
                <span className="font-mono font-bold text-foreground">{fmt$(effectivePrice)}</span> ={' '}
                <span className="font-mono font-bold text-fuchsia-700 dark:text-fuchsia-300">{fmt$(Number(shares) * effectivePrice)}</span> notional.
              </p>

              <MetricRow
                label="Blend yield"
                before={fmtPct(preview.before.dividend_yield)}
                after={fmtPct(preview.after.dividend_yield)}
                delta={preview.delta.dividend_yield}
                kind="pct"
                tip={`Blend Yield = weighted average dividend yield across every position in your portfolio (weighted by market value). Buying ${Number(shares).toLocaleString()} sh of ${ticker} shifts the average because ${ticker}'s own yield ${effectivePrice > 0 ? `at $${effectivePrice.toFixed(2)}` : ''} gets folded into the pool. If ${ticker} pays no dividend, adding it dilutes your blend yield (moves toward zero). If it pays more than your current blend, yield rises.`}
              />
              <MetricRow
                label="Annual income"
                before={fmt$(preview.before.annual_dividend_income)}
                after={fmt$(preview.after.annual_dividend_income)}
                delta={preview.delta.annual_dividend_income}
                kind="money"
                tip={`Annual Dividend Income = the total $ your portfolio would pay you in dividends over the next 12 months at current rates (shares × dividend_rate summed across positions). Adding ${ticker} bumps this only if ${ticker} pays a dividend; non-dividend stocks like ${ticker.match(/^(TSLA|AMZN|GOOGL|META|NFLX|NVDA)$/) ? ticker : 'this one'} leave it unchanged (delta $0).`}
              />
              {preview.before.quality_score != null && (
                <MetricRow
                  label="Blend quality"
                  before={`${preview.before.quality_score.toFixed(1)}/100`}
                  after={preview.after.quality_score != null ? `${preview.after.quality_score.toFixed(1)}/100` : '—'}
                  delta={preview.delta.quality_score}
                  kind="raw"
                  tip={`Blend Quality = market-value-weighted quality score across your positions (0-100, higher is better). Each stock's quality mixes dividend safety, growth, revenue stability and payout ratio. Adding ${ticker} (quality ~${preview.after.quality_score?.toFixed(0) ?? '?'}) either raises or lowers your blend depending on how it compares to your current average.`}
                />
              )}

              {/* Round 96m (Jul 2026) — Break-even calculator. Only
                  rendered for DIVIDEND-paying tickers (yield > 0) per
                  user request — non-dividend stocks like TSLA / AMZN
                  never break even on dividends alone, so showing a
                  ∞-year row for them would be noise.
                  Round 96n (Jul 2026) — Added third "with growth"
                  column that factors in the ticker's 5-yr dividend
                  CAGR. Iterative simulation:
                    year t → dividend_yr = shares × div_per_share_t
                    reinvest income at flat price → shares grow
                    div_per_share_t+1 = div_per_share_t × (1+CAGR)
                  Returns fractional year when cumulative income
                  first equals the initial notional cost. Aristocrats
                  growing at 6-8%/yr break even ~3-5 yr sooner than
                  the flat-yield DRIP model. */}
              {Number(dividendYield) > 0 && (() => {
                const yPct = Number(dividendYield);
                const gPctRaw = Number(dividendCagr5y);
                // Round P132 (Feb 2026) — Cap the growth rate used in
                // the payback simulation at a realistic ceiling.
                //
                // The 5-yr dividend CAGR field can legitimately return
                // absurd rates (49.8%, 82%, even 100%+) when the
                // underlying stock is:
                //   • A CEF classifying return-of-capital or special
                //     distributions as "growth" (ASGI here)
                //   • A recent dividend initiator with a tiny base year
                //   • A payer that resumed after a suspension
                //
                // Compounding a 49.8% CAGR through DRIP reinvestment
                // over 20 years produced $1.9 sextillion. Even for
                // the fastest legitimate dividend growers (early
                // NVDA, VICI, tech aristocrats), a sustained CAGR
                // above ~15%/yr is essentially unheard of over any
                // decadal window.
                //
                // Cap at 15% for the simulation only; display the
                // uncapped CAGR next to the button as-is so users
                // still see the raw stat.
                const GROWTH_CAP_PCT = 15;
                const gPctCapped = isFinite(gPctRaw) ? Math.min(gPctRaw, GROWTH_CAP_PCT) : gPctRaw;
                const growthWasCapped = isFinite(gPctRaw) && gPctRaw > GROWTH_CAP_PCT;
                const simple = 100 / yPct;
                const drip = Math.log(2) / Math.log(1 + yPct / 100);
                // Iterative year-by-year cumulative income for the
                // sparkline (below) and the "with growth" payback
                // (below-below).
                const buildTimeline = (growth) => {
                  const y = yPct / 100;
                  const g = (growth || 0) / 100;
                  let shares = 1;
                  let divPerShare = y;
                  let cum = 0;
                  const pts = [{ t: 0, cum: 0 }];
                  const MAX = 60;  // 60-yr horizon for the chart
                  let payback = null;
                  for (let t = 1; t <= MAX; t++) {
                    const income = shares * divPerShare;
                    if (payback == null && cum + income >= 1) {
                      payback = (t - 1) + (1 - cum) / income;
                    }
                    cum += income;
                    pts.push({ t, cum });
                    shares += income;
                    divPerShare *= (1 + g);
                  }
                  return { pts, payback };
                };
                const flat = buildTimeline(0);
                const withGrowth = isFinite(gPctCapped) ? buildTimeline(gPctCapped) : null;
                const growthPayback = withGrowth?.payback ?? null;
                const yrs = (n) => n == null ? '—' : n >= 100 ? '100+' : n >= 10 ? n.toFixed(1) : n.toFixed(2);
                const hasGrowth = isFinite(gPctCapped) && growthPayback != null;
                const notional = Number(shares) * effectivePrice;
                return (
                  <>
                    <MetricRow
                      label="Payback (years)"
                      before={`${yrs(simple)} yr`}
                      after={`${yrs(drip)} yr`}
                      delta={simple - drip}
                      kind="raw"
                      tip={`Break-even = how long until ${ticker}'s dividends alone repay the shares' purchase price at today's yield of ${yPct.toFixed(2)}%. "Before" column is the SIMPLE model (flat dividends, no reinvestment) = 100 ÷ yield%. "After" column is the DRIP model (dividends reinvested at the same yield, compounding into more shares) = ln(2) / ln(1 + yield/100). DRIP always finishes sooner (positive green delta) because each reinvested payment starts earning its own dividends. Assumes yield stays constant.`}
                    />
                    {/* Round 96v (Jul 2026) — Low-yield callout.
                        Under 3% yield the flat-DRIP break-even
                        stretches past ~24 yr — beyond most users'
                        working horizon. Nudge them toward growth-
                        first thinking so a low starting yield with
                        strong dividend CAGR doesn't get dismissed
                        as "bad payback". */}
                    {yPct < 3 && (
                      <p className="mt-0.5 mb-1 text-[10px] font-semibold text-fuchsia-600 dark:text-fuchsia-400 italic leading-snug px-2.5" data-testid="trade-preview-lowyield-note">
                        Yield below 3% — flat-DRIP payback stretches beyond a working lifetime.
                        {hasGrowth ? ` Focus on the +${gPctCapped.toFixed(1)}% growth row below; ${ticker}'s rising dividend is where the compounding really happens.` : ' Prefer growth-oriented sizing rather than pure yield-driven break-even.'}
                      </p>
                    )}
                    {hasGrowth && (
                      <MetricRow
                        label={`Payback w/ ${gPctCapped >= 0 ? '+' : ''}${gPctCapped.toFixed(1)}% growth`}
                        before={`${yrs(drip)} yr`}
                        after={`${yrs(growthPayback)} yr`}
                        delta={drip - growthPayback}
                        kind="raw"
                        tip={growthWasCapped
                          ? `${ticker}'s reported 5-yr dividend CAGR is ${gPctRaw.toFixed(1)}%/yr — unrealistic for long-term compounding (usually caused by return-of-capital, special distributions, or a small base year). We CAPPED the growth rate at ${GROWTH_CAP_PCT}%/yr for the payback simulation so the numbers stay believable. Even the fastest legitimate dividend growers rarely sustain above 15%/yr over a decade.`
                          : `Same DRIP model as above BUT with ${ticker}'s dividend growing at its ${gPctCapped.toFixed(2)}% 5-year CAGR each year. Every reinvested share earns a slightly bigger dividend than the last, so payback shortens by ${Math.abs(drip - growthPayback).toFixed(2)} yr vs the flat-yield model. Aristocrats growing 6-8%/yr typically shave 3-5 yr off DRIP payback. Negative growth (dividend cuts) does the opposite — expect a longer payback than flat.`}
                        chartOpen={chartOpen}
                        onToggleChart={() => setChartOpen((o) => !o)}
                      />
                    )}
                    {growthWasCapped && (
                      <p className="mt-0.5 mb-1 text-[10px] font-semibold text-amber-600 dark:text-amber-400 italic leading-snug px-2.5" data-testid="trade-preview-growth-capped-note">
                        Growth capped at {GROWTH_CAP_PCT}%/yr — {ticker}&apos;s reported {gPctRaw.toFixed(1)}% 5-yr CAGR looks inflated (likely from special distributions / return of capital). No dividend grows that fast for long.
                      </p>
                    )}
                    {/* Round 96s (Jul 2026) — Sparkline is now
                        collapsible and defaults to CLOSED. Users
                        toggle open/close via the chevron button on
                        the growth-payback row above. Reason: the
                        chart is ~130 px tall and used to push the
                        "Add Position" form completely below the
                        viewport on the Stock Modal → My Portfolio
                        tab. */}
                    {chartOpen && (
                      <PaybackSparkline
                        flat={flat}
                        withGrowth={withGrowth}
                        dripPayback={drip}
                        growthPayback={growthPayback}
                        yieldPct={yPct}
                        growthPct={isFinite(gPctCapped) ? gPctCapped : null}
                        ticker={ticker}
                        notional={notional}
                      />
                    )}
                  </>
                );
              })()}

              {/* Round 96l (Jul 2026) — Explainer footer. Explains
                  the arrow chips + the "0.00" outcomes users see for
                  non-dividend stocks like TSLA at small share
                  counts. Reuses the "cursor-help" pattern so the
                  paragraph itself doubles as a hoverable summary.
                  Round 96w (Jul 2026) — Info icon added inline with
                  "How to read this:" label to match the fuchsia
                  Calculator icon in the card title. Wrapper is now
                  a rounded chip so the block reads as a coordinated
                  callout, not a lone paragraph. */}
              <div className="mt-2 pt-2 border-t border-fuchsia-500/20 text-[10px] text-muted-foreground leading-relaxed">
                <p>
                  <span className="inline-flex items-center gap-1 mr-1 align-baseline">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 shrink-0">
                      <Info className="w-2.5 h-2.5" strokeWidth={2.5} />
                    </span>
                    <span className="font-bold text-fuchsia-600 dark:text-fuchsia-400">How to read this:</span>
                  </span>{' '}
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Green ▲</span> = trade
                  would <em>improve</em> that metric; <span className="text-rose-600 dark:text-rose-400 font-semibold">red ▼</span> = would <em>hurt</em> it.
                  A <span className="font-mono">±0.00</span> means the trade is too small (or the stock is non-dividend for income) to move the number at the shown precision — try more shares to see a meaningful shift.
                  Hover a row for a metric-specific explanation.
                </p>

                {/* Round 96x (Jul 2026) — "Add this trade" quick
                    action. Bottom-right of the card so it doesn't
                    compete with the metric rows above. Bold text
                    (bigger than surrounding copy for eye-catch), and
                    forced high-contrast: bold BLACK in light theme,
                    bold WHITE in dark theme per user spec. Pre-fills
                    the Add Position form below with the shares +
                    price the user has been modelling and scrolls
                    the form into view. Self-hides if the user
                    hasn't typed any hypothetical shares yet. */}
                {onApplyTrade && Number(shares) > 0 && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onApplyTrade({ shares: Number(shares), price: Number(priceInput) || Number(currentPrice) || 0 })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-fuchsia-500/60 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 px-3 py-1.5 text-sm font-black text-black dark:text-white transition-colors shadow-sm hover:shadow-md active:scale-95"
                      data-testid="trade-preview-apply-btn"
                      title="Copy these shares & price into the Add Position form below"
                    >
                      <Calculator className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400" />
                      Add this trade
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {(!preview || !Number(shares)) && !loading && (
            <p className="text-[10px] text-muted-foreground italic">
              Type a share count above to see how buying <span className="font-mono font-bold text-foreground">{ticker}</span> would shift your blend.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Round 96o (Jul 2026) — Payback timeline sparkline. Two lines
// (DRIP-flat cumulative income + DRIP-with-growth cumulative income)
// plotted against a horizontal $1 reference line = break-even
// threshold. Uses SVG for zero-dependency rendering; the hover
// tooltip is anchored inside the card so it never overflows the
// stock modal.
function PaybackSparkline({ flat, withGrowth, dripPayback, growthPayback, yieldPct, growthPct, ticker, notional }) {
  const [hover, setHover] = useState(false);
  // Round 96q (Jul 2026) — Retirement-target slider. Default 20 yr
  // (a common personal-finance horizon). Users drag to any year
  // 1..60 to see cumulative income at that horizon on both curves.
  const [targetYear, setTargetYear] = useState(20);
  if (!flat || !flat.pts || flat.pts.length === 0) return null;
  const W = 320;
  const H = 78;
  const padX = 4;
  const padY = 14;
  // X-axis = year 0..60. Y-axis = cumulative income normalized so
  // 1.0 = notional cost. Cap the y range at 1.5× the reference line
  // so the break-even crossing sits nicely in the middle of the
  // chart.
  const maxT = flat.pts[flat.pts.length - 1].t;
  const yCap = 1.5;
  const xOf = (t) => padX + (t / maxT) * (W - 2 * padX);
  const yOf = (c) => H - padY - (Math.min(c, yCap) / yCap) * (H - 2 * padY);
  const pathOf = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.t).toFixed(1)},${yOf(p.cum).toFixed(1)}`).join(' ');
  const refY = yOf(1.0);
  const flatCross = dripPayback != null && dripPayback <= maxT ? { x: xOf(dripPayback), y: refY } : null;
  const growthCross = growthPayback != null && growthPayback <= maxT ? { x: xOf(growthPayback), y: refY } : null;
  // Cumulative income at the retirement target year (linear interp
  // between the two nearest integer-year data points). Used both
  // for the SVG marker and the numeric readout below the chart.
  const cumAt = (pts, yr) => {
    if (!pts || pts.length === 0) return null;
    const clamped = Math.max(0, Math.min(yr, maxT));
    const lo = Math.floor(clamped);
    const hi = Math.min(lo + 1, maxT);
    const a = pts[lo]?.cum ?? 0;
    const b = pts[hi]?.cum ?? a;
    return a + (b - a) * (clamped - lo);
  };
  const flatAtTarget = cumAt(flat.pts, targetYear);
  const growthAtTarget = withGrowth ? cumAt(withGrowth.pts, targetYear) : null;
  const targetX = xOf(targetYear);
  return (
    <div
      className="relative rounded-md bg-card px-2.5 py-2 cursor-help"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="trade-preview-payback-sparkline"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Payback timeline</span>
        <div className="flex items-center gap-2 text-[9px] font-mono">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5 bg-fuchsia-500" />
            <span className="text-fuchsia-700 dark:text-fuchsia-300">Flat DRIP</span>
          </span>
          {withGrowth && (
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 bg-emerald-500" />
              <span className="text-emerald-700 dark:text-emerald-300">+{growthPct?.toFixed(1)}% growth</span>
            </span>
          )}
        </div>
      </div>

      {/* Round 96t (Jul 2026) — Guaranteed-visible crossover
          readout. The in-SVG dot labels were being clipped by the
          amber slider vertical line + narrow viewBox at the top of
          the chart, so many users never saw the numeric payback
          annotations. This text block sits directly under the
          legend row and is always visible in the DOM, laid out with
          normal HTML flex so no clipping is possible. */}
      <div className="flex items-center justify-between gap-2 mb-1 text-[10px] font-mono">
        {dripPayback != null && dripPayback <= 100 && (
          <span className="text-fuchsia-700 dark:text-fuchsia-300">
            Flat crosses at <span className="font-bold">{dripPayback.toFixed(1)} yr</span>
            {Number(notional) > 0 && (
              <> · <span className="font-bold">${Number(notional).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> recovered</>
            )}
          </span>
        )}
        {growthPayback != null && growthPayback <= 100 && (
          <span className="text-emerald-700 dark:text-emerald-300">
            Growth crosses at <span className="font-bold">{growthPayback.toFixed(1)} yr</span>
            {Number(notional) > 0 && (
              <> · <span className="font-bold">${Number(notional).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> recovered</>
            )}
          </span>
        )}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        {/* Break-even reference line at cumulative income = $1 (notional cost). */}
        <line x1={padX} y1={refY} x2={W - padX} y2={refY} stroke="currentColor" strokeDasharray="2,3" strokeOpacity="0.35" strokeWidth="1" />
        <text x={W - padX - 2} y={refY - 3} textAnchor="end" fill="currentColor" fillOpacity="0.5" fontSize="8" fontFamily="monospace">notional</text>

        {/* Round 96q (Jul 2026) — "Today" vertical marker at year 0.
            Fuchsia dashed line + tiny "Today" label so users
            instantly locate the starting point of the timeline. */}
        <line x1={xOf(0)} y1={padY} x2={xOf(0)} y2={H - padY} stroke="rgb(217 70 239)" strokeDasharray="1.5,2" strokeOpacity="0.6" strokeWidth="1" />
        <text x={xOf(0) + 3} y={padY + 6} fill="rgb(217 70 239)" fillOpacity="0.75" fontSize="7" fontFamily="monospace" fontWeight="700">TODAY</text>
        {/* Year gridlines every 10 yr + numeric year labels on the
            X-axis so users can read where each dot sits (e.g. the
            emerald dot at ~16 yr, fuchsia dot at ~22 yr). Round 96p
            (Jul 2026) — user asked for actual year values, not just
            "yr" at the ends. */}
        {[10, 20, 30, 40, 50].map((yr) => (
          <g key={yr}>
            <line x1={xOf(yr)} y1={padY} x2={xOf(yr)} y2={H - padY} stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
            <text x={xOf(yr)} y={H - 2} textAnchor="middle" fill="currentColor" fillOpacity="0.55" fontSize="8" fontFamily="monospace">{yr}</text>
          </g>
        ))}
        {/* Flat DRIP curve. */}
        <path d={pathOf(flat.pts)} stroke="rgb(217 70 239)" strokeWidth="1.5" fill="none" />
        {/* With-growth curve (only if we have CAGR). */}
        {withGrowth && (
          <path d={pathOf(withGrowth.pts)} stroke="rgb(16 185 129)" strokeWidth="1.5" fill="none" />
        )}
        {/* Payback crossover markers with LEADER-LINE callouts.
            Round P132 (Feb 2026) — Labels used to be stacked directly
            above / below each dot, which caused hard collisions
            whenever both crossovers landed near the same X position
            (e.g. ASGI: growth at 3.7 yr, flat at 6.7 yr sit right on
            top of each other on a 60-yr X-scale). The old text also
            overlapped the amber Target vertical line at 20 yr.

            New scheme:
              • GROWTH (emerald): thin vertical leader drops from the
                dot down to the bottom axis, then a short horizontal
                arrow points right into the label — e.g.
                "→  $239 in 3.7 yr". Text sits BELOW the chart
                proper, near the X-axis, so it never crashes into the
                flat callout.
              • FLAT (fuchsia): short horizontal leader with a chevron
                shoots to the right of the dot and the label follows
                inline — e.g. "→  $239 in 6.7 yr". Text sits at the
                same Y as the notional reference line, above the
                growth callout.
            The two callouts are visually distinct AND cannot overlap
            because they emerge from the marker at different angles. */}
        {growthCross && (() => {
          const notionalStr = Number(notional) > 0
            ? `$${Number(notional).toLocaleString(undefined, { maximumFractionDigits: 0 })} in ${growthPayback.toFixed(1)} yr`
            : `${growthPayback.toFixed(1)} yr`;
          // Round P132d (Feb 2026) — Shortened green horizontal
          // leader to 2× the pink leader length (20 vs 10 units)
          // and halved the callout font sizes per user request.
          const flip = growthCross.x > W * 0.6;
          const labelX = growthCross.x + (flip ? -22 : 22);
          const arrowStartX = growthCross.x + (flip ? -3 : 3);
          const arrowEndX = labelX + (flip ? 3 : -3);
          const dropY = H - padY - 4;
          return (
            <g data-testid="payback-growth-callout">
              {/* Vertical leader from dot straight down to axis. */}
              <line x1={growthCross.x} y1={growthCross.y + 3} x2={growthCross.x} y2={dropY} stroke="rgb(16 185 129)" strokeWidth="0.6" strokeOpacity="0.75" />
              {/* Horizontal leader + arrow head to the label. */}
              <line x1={arrowStartX} y1={dropY} x2={arrowEndX} y2={dropY} stroke="rgb(16 185 129)" strokeWidth="0.6" strokeOpacity="0.75" />
              <polyline
                points={flip
                  ? `${arrowEndX + 2},${dropY - 2} ${arrowEndX},${dropY} ${arrowEndX + 2},${dropY + 2}`
                  : `${arrowEndX - 2},${dropY - 2} ${arrowEndX},${dropY} ${arrowEndX - 2},${dropY + 2}`}
                fill="none"
                stroke="rgb(16 185 129)"
                strokeWidth="0.6"
              />
              <circle cx={growthCross.x} cy={growthCross.y} r="3" fill="rgb(16 185 129)" stroke="white" strokeWidth="1" />
              <text
                x={labelX}
                y={dropY + 2}
                textAnchor={flip ? 'end' : 'start'}
                fill="rgb(16 185 129)"
                fontSize="6"
                fontWeight="700"
                fontFamily="monospace"
              >
                {notionalStr}
              </text>
            </g>
          );
        })()}
        {flatCross && (() => {
          const notionalStr = Number(notional) > 0
            ? `$${Number(notional).toLocaleString(undefined, { maximumFractionDigits: 0 })} in ${dripPayback.toFixed(1)} yr`
            : `${dripPayback.toFixed(1)} yr`;
          // Flat callout goes HORIZONTAL to the right of the dot with
          // a chevron. Auto-flip left near the right edge.
          // Round P132d — 10-unit leader; text halved to fontSize=4.
          const flip = flatCross.x > W * 0.55;
          const leaderStartX = flatCross.x + (flip ? -3 : 3);
          const leaderEndX = flatCross.x + (flip ? -10 : 10);
          const labelX = leaderEndX + (flip ? -2 : 2);
          return (
            <g data-testid="payback-flat-callout">
              {/* Short horizontal leader from the dot toward the label. */}
              <line x1={leaderStartX} y1={flatCross.y} x2={leaderEndX} y2={flatCross.y} stroke="rgb(217 70 239)" strokeWidth="0.6" strokeOpacity="0.75" />
              {/* Chevron/arrow at the leader tip. */}
              <polyline
                points={flip
                  ? `${leaderEndX + 2},${flatCross.y - 2} ${leaderEndX},${flatCross.y} ${leaderEndX + 2},${flatCross.y + 2}`
                  : `${leaderEndX - 2},${flatCross.y - 2} ${leaderEndX},${flatCross.y} ${leaderEndX - 2},${flatCross.y + 2}`}
                fill="none"
                stroke="rgb(217 70 239)"
                strokeWidth="0.6"
              />
              <circle cx={flatCross.x} cy={flatCross.y} r="3" fill="rgb(217 70 239)" stroke="white" strokeWidth="1" />
              <text
                x={labelX}
                y={flatCross.y + 2}
                textAnchor={flip ? 'end' : 'start'}
                fill="rgb(217 70 239)"
                fontSize="6"
                fontWeight="700"
                fontFamily="monospace"
              >
                {notionalStr}
              </text>
            </g>
          );
        })()}
        {/* Start / end year labels — spelled out with the actual
            numeric year (was previously showing just "yr" clipped). */}
        <text x={padX} y={H - 2} fill="currentColor" fillOpacity="0.55" fontSize="8" fontFamily="monospace">0 yr</text>
        <text x={W - padX} y={H - 2} textAnchor="end" fill="currentColor" fillOpacity="0.55" fontSize="8" fontFamily="monospace">{maxT} yr</text>

        {/* Round 96q (Jul 2026) — Retirement-target vertical line +
            markers on both curves at the target year. Amber so it
            reads distinctly from the fuchsia / emerald curves. Dots
            snap to the exact cumulative-income value of each line
            at the chosen year (linear interp between the two
            nearest integer years). */}
        <line x1={targetX} y1={padY} x2={targetX} y2={H - padY} stroke="rgb(245 158 11)" strokeOpacity="0.85" strokeWidth="1.5" />
        {flatAtTarget != null && (
          <circle cx={targetX} cy={yOf(flatAtTarget)} r="2.5" fill="rgb(245 158 11)" stroke="rgb(217 70 239)" strokeWidth="1" />
        )}
        {growthAtTarget != null && (
          <circle cx={targetX} cy={yOf(growthAtTarget)} r="2.5" fill="rgb(245 158 11)" stroke="rgb(16 185 129)" strokeWidth="1" />
        )}
        <text
          x={targetX + (targetX > W - 50 ? -3 : 3)}
          y={padY - 2}
          textAnchor={targetX > W - 50 ? 'end' : 'start'}
          fill="rgb(245 158 11)"
          fillOpacity="0.95"
          fontSize="9"
          fontWeight="700"
          fontFamily="monospace"
        >
          @ {targetYear} yr
        </text>
      </svg>

      {/* Round 96q (Jul 2026) — Retirement horizon slider + readout.
          Native range input for maximum accessibility (keyboard
          arrows work, mobile scrubs cleanly). Below the slider a
          two-line readout shows the cumulative income at the chosen
          year on both curves as a % of the original cost — i.e.
          "at year 20, DRIP = 87% recovered, w/ growth = 132%
          (paid off + 32% ahead)". */}
      <div className="mt-1 flex items-center gap-2">
        <label className="text-[9px] uppercase tracking-wider font-bold text-amber-600 dark:text-amber-400 shrink-0">
          Target
        </label>
        <input
          type="range"
          min="1"
          max={maxT}
          step="1"
          value={targetYear}
          onChange={(e) => setTargetYear(Number(e.target.value))}
          className="flex-1 h-3 accent-amber-500 cursor-pointer"
          data-testid="payback-sparkline-target-slider"
          aria-label="Retirement target year"
        />
        <span className="text-[10px] font-mono font-bold text-amber-600 dark:text-amber-400 shrink-0 tabular-nums" data-testid="payback-sparkline-target-year">
          {targetYear} yr
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] font-mono">
        {/* Round 96r (Jul 2026) — Cumulative income readouts are now
            in ACTUAL DOLLARS (fraction of cost × notional cost)
            instead of "% of cost". The percentage was confusing when
            growth models pushed the value past 100% ("132% of cost"
            reads weirder than "$2,780 of $2,126 back"). */}
        <span className="text-fuchsia-700 dark:text-fuchsia-300">
          Flat DRIP: <span className="font-bold">
            {flatAtTarget != null && Number(notional) > 0
              ? `$${(flatAtTarget * Number(notional)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
              : '—'}
          </span>
          <span className="opacity-60"> received</span>
        </span>
        {growthAtTarget != null && (
          <span className="text-emerald-700 dark:text-emerald-300">
            w/ Growth: <span className="font-bold">
              {Number(notional) > 0
                ? (() => {
                    // Round P132 (Feb 2026) — Sanity guard against
                    // impossibly-large numbers. If the growth
                    // simulation ever escaped the CAGR cap and
                    // produced a display value beyond ~$1 M / $1 of
                    // notional (i.e. a million-fold return), we
                    // clamp to a "> $1M" string rather than
                    // rendering "$1,913,534,099,989,286,400,000". No
                    // legitimate dividend model returns that in a
                    // 20-yr horizon.
                    const dollars = growthAtTarget * Number(notional);
                    if (!Number.isFinite(dollars) || dollars > 1e7 * Number(notional)) {
                      return '> $10M';
                    }
                    return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                  })()
                : '—'}
            </span>
            <span className="opacity-60"> received</span>
          </span>
        )}
      </div>
      {Number(notional) > 0 && (
        <p className="mt-1 text-[9px] text-muted-foreground text-center">
          vs. <span className="font-mono">${Number(notional).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span> initial cost
        </p>
      )}
      {hover && (
        <div
          className="absolute left-1 right-1 top-full mt-1 z-50 rounded-md border border-fuchsia-500/40 bg-popover text-popover-foreground px-3 py-2 text-[11px] leading-relaxed shadow-xl pointer-events-none"
          role="tooltip"
        >
          Cumulative dividend income (as a fraction of the initial notional cost) over the next {maxT} years for {ticker}.
          The horizontal dashed line is the <span className="font-semibold">break-even</span> threshold — where cumulative income equals what you paid.
          <span className="text-fuchsia-700 dark:text-fuchsia-300 font-semibold"> Fuchsia</span> = flat DRIP (yield stays at {yieldPct.toFixed(2)}%);
          {growthPct != null && (
            <> <span className="text-emerald-700 dark:text-emerald-300 font-semibold">emerald</span> = with the dividend growing at {growthPct.toFixed(2)}%/yr.</>
          )}
          Dots on the notional line mark automatic payback. The <span className="text-amber-600 dark:text-amber-400 font-semibold">amber vertical line</span> is your Target horizon — drag the slider to see cumulative income at any year 1–{maxT}.
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, before, after, delta, kind, tip, chartOpen, onToggleChart }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="relative flex items-center justify-between gap-2 flex-wrap rounded-md bg-card px-2.5 py-1.5 cursor-help"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      tabIndex={tip ? 0 : -1}
      data-testid={`trade-preview-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono text-muted-foreground">{before}</span>
        <span className="text-[10px] text-muted-foreground">→</span>
        <span className="text-xs font-mono font-bold text-foreground">{after}</span>
        <DeltaChip value={delta} kind={kind} />
        {/* Round 96s (Jul 2026) — Optional chart toggle. Only
            rendered on the "Payback w/ growth" row so users can
            expand/collapse the sparkline without losing sight of
            the Add Position form. */}
        {onToggleChart && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleChart(); }}
            className="inline-flex items-center gap-0.5 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
            data-testid="trade-preview-toggle-chart"
            title={chartOpen ? 'Hide the payback timeline chart' : 'Show the payback timeline chart'}
          >
            {chartOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {chartOpen ? 'Hide chart' : 'Show chart'}
          </button>
        )}
      </div>
      {/* Round 96l (Jul 2026) — In-modal tooltip. Uses left/right/top
          zero offsets so the popover is anchored to the row's own
          width; text wraps naturally and never overflows the modal
          horizontally. Mirrors the growth-tab/today-chart tooltip
          pattern the user pointed at. */}
      {hover && tip && (
        <div
          className="absolute left-1 right-1 top-full mt-1 z-50 rounded-md border border-fuchsia-500/40 bg-popover text-popover-foreground px-3 py-2 text-[11px] leading-relaxed shadow-xl pointer-events-none"
          role="tooltip"
        >
          {tip}
        </div>
      )}
    </div>
  );
}
